import { isMembershipSetupFeeLine } from '@notifi/shared-types/membership-line';
import type { Estimate, EstimateMembershipOffer } from '@/types/estimate';
import type { Proposal, ProposalCompany, ProposalCustomer, ProposalOption } from '@/types/proposal';
import type { EstimateDocTotalsOverride } from '@/components/documents/estimates/blocks/EstimateDocumentRenderer';
import {
  buildDefaultEstimateLayout,
  ensureEstimateScopeBlock,
} from '@/components/documents/estimates/templateBuilder/defaultLayout';
import type { ResolvedCustomerTerms } from '@/lib/terms/clientTerms';
import { resolveEstimateLineAmounts } from './line-item-pricing';
import { chargesRevenue } from './line-economics';
import { proposalOptionBreakdown, splitProposalLines } from './proposalOptionBreakdown';
import {
  proposalOptionMembershipSavingsPlanNames,
  proposalOptionSelectedBreakdown,
} from './proposalOptionMembership';

export type ProposalOptionDocumentSource = Pick<
  Proposal,
  | 'id'
  | 'proposalNumber'
  | 'serviceAddress'
  | 'billingAddress'
  | 'validUntil'
  | 'notes'
  | 'terms'
  | 'timezone'
  | 'resolvedDocLayout'
> & {
  customer?: (Omit<Partial<ProposalCustomer>, 'name'> & { name?: string | null }) | null;
  company?: (Omit<Partial<ProposalCompany>, 'name'> & { name?: string | null }) | null;
  createdAt?: string | null;
};

/** A render-only projection. Money stays owned by the proposal's pricing helpers;
 * this never creates an estimate or derives a price from catalog/cost fields. */
export function buildProposalOptionEstimate({
  proposal,
  option,
  optionLabel,
  offer,
  membershipSelected,
  displayTotal,
  depositDue,
  terms,
}: {
  proposal: ProposalOptionDocumentSource;
  option: ProposalOption;
  optionLabel: string;
  offer: EstimateMembershipOffer | null;
  membershipSelected: boolean;
  displayTotal: number;
  depositDue: number;
  terms: ResolvedCustomerTerms;
}) {
  const stored = proposalOptionBreakdown(option);
  const breakdown = proposalOptionSelectedBreakdown(option, membershipSelected, offer);
  const takingOffer = membershipSelected && offer?.planPrice != null;
  const total = option.selectionPricing?.total ?? displayTotal;
  const deposit = option.selectionPricing?.depositDue ?? depositDue;
  const setupFee = splitProposalLines(option)
    .required.filter(
      (line) => isMembershipSetupFeeLine(line) && line.displayConfig?.hidePrice !== true,
    )
    .reduce(
      (sum, line) => sum + (line.lineListTotal ?? resolveEstimateLineAmounts(line).lineSubtotal),
      0,
    );
  const savingsNames = proposalOptionMembershipSavingsPlanNames(
    option,
    membershipSelected,
    offer,
    stored.membershipSavings,
  );
  const totalsOverride: EstimateDocTotalsOverride = {
    subtotal: breakdown.subtotal - setupFee,
    membershipSavings: breakdown.membershipSavings,
    membershipPlanPrice: takingOffer ? offer.planPrice! : 0,
    membershipSetupFee: setupFee + (takingOffer ? (offer.setupFee ?? 0) : 0),
    membershipPlanLabel: offer?.planName ?? 'Membership',
    // A public projection may omit the held plan's name. Do not credit all
    // existing savings to the new plan merely because its name is available.
    membershipSavingsLabel: savingsNames || 'All plans',
    discountTotal: breakdown.discount,
    discountLabel: 'Discount',
    // A proposal can omit hidden priced rows; summing their visible tax
    // snapshots cannot establish a complete jurisdiction breakdown.
    taxBreakdown: [],
    taxTotal: breakdown.tax,
    grandTotal: total,
    depositAmount: deposit,
  };

  // The block engine reads this subset of Estimate. Preserve raw lines and
  // identities for bundles, visibility and frozen membership adjustments.
  // Legacy proposals treat an absent selection flag as selected.
  const estimate = {
    id: `${proposal.id}:${option.optionKey ?? optionLabel}`,
    estimateNumber: proposal.proposalNumber,
    title: optionLabel,
    scopeOfWork: option.description ?? null,
    customer: proposal.customer,
    serviceAddress: proposal.serviceAddress,
    billingAddress: proposal.billingAddress,
    createdAt: proposal.createdAt,
    validUntil: proposal.validUntil,
    notes: proposal.notes,
    termsAndConditions: terms.text,
    lineItems: (option.lineItems ?? []).map((line) => ({
      ...line,
      isSelected: line.isSelected !== false,
      // Proposals exclude cost-only parts from the customer's offer even when
      // an internal preview carries them. Keep their position/identity for
      // frozen adjustments, but do not broaden that public projection.
      ...(!chargesRevenue(line) ? { visibleToCustomer: false, hiddenFromCustomer: true } : {}),
    })),
    subtotal: breakdown.subtotal,
    taxTotal: breakdown.tax,
    discountTotal: breakdown.discount,
    membershipDiscountTotal: breakdown.membershipSavings,
    membershipPlanSnapshot: option.membershipPlanSnapshot,
    grandTotal: total,
    // Keep the projected estimate and the totals/deposit blocks consistent.
    depositAmount: deposit,
    depositCollected: false,
  } as unknown as Estimate;

  const resolved = proposal.resolvedDocLayout;
  const base =
    resolved && Array.isArray(resolved.blocks) && resolved.blocks.length > 0
      ? resolved
      : buildDefaultEstimateLayout();
  const layout = ensureEstimateScopeBlock({
    ...base,
    blocks: base.blocks.map((block) => {
      if (block.kind !== 'content' || block.type !== 'estimateDetails') return block;
      return {
        ...block,
        fields: {
          ...block.fields,
          section: {
            ...block.fields?.section,
            label: block.fields?.section?.label ?? 'Proposal',
          },
          estimateNumber: {
            ...block.fields?.estimateNumber,
            label: block.fields?.estimateNumber?.label ?? 'Proposal #',
          },
        },
      };
    }),
  });
  return { estimate, totalsOverride, layout };
}
